import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import NextApp from 'next/app';
import React from 'react';
import { AppCacheProvider } from '@mui/material-nextjs/v13-pagesRouter';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';

import '@/globals.css';
import '@/mui-variables.css';
import GlobalCSS from '@/components/global-css';
import { GoogleAnalytics } from '@/components/google-analytics';
import muiTheme from '@/constants/mui-theme';
import styledTheme from '@/constants/styled-theme';
import { circularBlack, circularBook, circularPro } from '@/utils/fonts';
import { cn } from '@/lib/utils';
import { logPageViewEvent } from '@/utils/tracking';

import type { AppProps } from 'next/app';

class App extends NextApp {
  componentDidMount() {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'jssStyles.parentNode' is possibly 'null'.
      jssStyles.parentNode.removeChild(jssStyles);
    }

    this.logContentForCurrentPage();
  }

  componentDidUpdate(prevProps: AppProps) {
    if (prevProps.pageProps !== this.props.pageProps) {
      this.logContentForCurrentPage();
    }
  }

  logContentForCurrentPage() {
    const meta = this.props.pageProps.trackingMeta ?? {
      content_type: 'informational',
      content_group: 'unknown',
    };
    logPageViewEvent(meta);
  }

  render() {
    const { Component, pageProps } = this.props;

    // Deep merge the themes to avoid overriding nested properties
    const combinedTheme = { ...muiTheme, ...styledTheme };

    return (
      <AppCacheProvider {...this.props}>
        <div className={cn(circularPro.variable, circularBlack.variable, circularBook.variable)}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={combinedTheme}>
              {/* CssBaseline kickstarts an elegant, consistent, and simple baseline to build upon. */}
              <CssBaseline />
              <GlobalCSS />
              <GoogleAnalytics />
              <NuqsAdapter>
                <Component {...pageProps} {...this.state} />
              </NuqsAdapter>
            </ThemeProvider>
          </StyledEngineProvider>
        </div>
      </AppCacheProvider>
    );
  }
}

export default App;
